import React, { useEffect, useState, useMemo } from 'react';
import { compose } from 'redux-starter-kit';
import { connect } from 'react-redux';
import Store from 'store/store';
import Grid from '@material-ui/core/Grid';
import { makeStyles, CircularProgress } from '@material-ui/core';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import api from 'api';
import PersistentTable from './PersistentTable';
import {
  resetTableState,
  getCachableData,
  getFundColumns,
  getModelPortfolioColumns
} from './utils';
import { useTheme } from '@material-ui/styles';
import PortfolioAllocationsModal from './PortfolioAllocationsModal';
import { MRT_ColumnDef } from 'material-react-table';

const { selectors, actions } = Store;

const useStyles = makeStyles({
  loaderWrapper: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100%',
    minHeight: '25rem'
  }
});

interface OverviewTabContentProps {
  investments: any[];
  riskRatings: any;
  riskRatingsCacheIsValid: boolean;
  updateRiskRatings: Function;
  riskProfiler: string;
  selectedInvestment?: number;
  provider?: any;
  range?: any;
}

const OverviewTabContent = ({
  investments,
  riskRatings,
  riskRatingsCacheIsValid,
  updateRiskRatings,
  selectedInvestment,
  provider,
  range,
  riskProfiler
}: OverviewTabContentProps) => {
  const theme = useTheme() as any;
  const classes = useStyles();
  const [funds, setFunds] = useState([] as any[]);
  const [modalOpen, setModalOpen] = useState(false);
  const [parentMpName, setParentMpName] = useState('');
  const [parentRowData, setParentRowData] = useState([] as any[]);
  const [rowData, setRowData] = useState([] as any[]);
  const [modelPortfolioFunds, setModelPortfolioFunds] = useState([] as any[]);
  const [nestedModelPortfolios, setNestedModelPortfolios] = useState(
    [] as any[]
  );

  const [selectedMpName, setSelectedMpName] = useState('');
  const [isLoading, setIsLoading] = useState(!riskRatings.investments.length);

  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!selectedInvestment && !provider) return;
    selectedInvestment
      ? setFunds(investments.filter((investment: any) => investment.is_checked))
      : setFunds(investments);
    getCachableData(
      investments,
      riskRatingsCacheIsValid,
      setIsLoading,
      selectedInvestment
        ? () => api.getResearchItemRiskRatings(selectedInvestment)
        : () => api.getMpsHubRiskRatings(provider, range),
      updateRiskRatings
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [investments]);

  useEffect(() => {
    setModelPortfolioFunds(
      rowData.filter((item: any) => !!item.fund || item.type === 'f')
    );
    setNestedModelPortfolios(
      rowData.filter(
        (item: any) => ('key' in item && !item.fund) || item.type === 'mp'
      )
    );
  }, [rowData]);

  const goBackToParentMp = () => {
    setSelectedMpName(parentMpName);
    setRowData(parentRowData);
    setParentMpName('');
    setParentRowData([]);
  };

  const fundColumns = getFundColumns({
    riskProfiler,
    includeAllocation: true,
    riskRatings,
    ocf_field: 'original_ocf',
    theme
  });
  const columns = useMemo<MRT_ColumnDef<any>[]>(
    () => fundColumns as any,
    [investments, isLoading]
  );

  const modelPortfolioColumns = getModelPortfolioColumns({
    riskProfiler,
    includeAllocation: true,
    riskRatings,
    ocf_field: 'original_ocf',
    theme
  });
  const mpColumns = useMemo<MRT_ColumnDef<any>[]>(
    () => modelPortfolioColumns as any,
    [investments, isLoading]
  );

  const data = useMemo(() => funds, [funds]);

  const handleRowClick = (params: any, map = false) => {
    if (
      (params.row.original.mp_id && params.column.id === 'type') ||
      params.column.id === 'name'
    ) {
      setModalOpen(true);
      if (params.row && params.row.original.name) {
        setSelectedMpName(params.row.original.name);
      }

      if (map) {
        setParentMpName(selectedMpName);
        setParentRowData(rowData);
        setRowData(
          params.row.original.fund_allocations.map((allocation: any) => ({
            ...allocation.fund,
            allocation: allocation.allocation,
            type: 'f'
          }))
        );
        return;
      }

      setParentRowData([]);
      setRowData(params.row.original.fund_allocations);
    }
  };

  const { fundsData, mpData } = data.reduce(
    (result, row) => {
      if (!row.mp_id) {
        result.fundsData.push(row);
      } else {
        result.mpData.push(row);
      }
      return result;
    },
    { fundsData: [], mpData: [] }
  );

  const reset = (key: string) => {
    resetTableState(key);
    setCount(count + 1);
  };

  const fundsTableId = `ia_case_${selectedInvestment}_overview_funds`;
  const mpTableId = `ia_case_${selectedInvestment}_overview_mps`;

  return (
    <>
      {isLoading ? (
        <div className={classes.loaderWrapper}>
          <CircularProgress size="80px" />
        </div>
      ) : (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <div style={{ paddingTop: '2rem' }}>
            <Grid container>
              {fundsData && fundsData.length > 0 && (
                <div style={{ width: '100%', marginBottom: '2rem' }}>
                  <h3>
                    Funds
                    <span
                      className="dashboardLink"
                      style={{ float: 'right', marginRight: '0.6rem' }}
                      onClick={() => reset(`${fundsTableId}`)}
                    >
                      Reset Filters
                    </span>
                  </h3>
                  <PersistentTable
                    key={`${fundsTableId}_${count}`}
                    tableId={fundsTableId}
                    columns={columns.filter(
                      (row: any) => row.accessorKey !== 'allocation'
                    )}
                    data={fundsData}
                  />
                </div>
              )}
              {mpData && mpData.length > 0 && (
                <div style={{ width: '100%' }}>
                  <h3>
                    Portfolios
                    <span
                      className="dashboardLink"
                      style={{ float: 'right', marginRight: '0.6rem' }}
                      onClick={() => reset(mpTableId)}
                    >
                      Reset Filters
                    </span>
                  </h3>
                  <PersistentTable
                    key={`${mpTableId}_${count}`}
                    tableId={mpTableId}
                    columns={mpColumns.filter(
                      (row: any) => row.accessorKey !== 'allocation'
                    )}
                    data={mpData.filter((row: any) => !row.loading)}
                    rowClick={handleRowClick}
                  />
                </div>
              )}
              <PortfolioAllocationsModal
                isOpen={modalOpen}
                setIsOpen={setModalOpen}
                columns={columns}
                mpColumns={mpColumns}
                modelPortfolioName={selectedMpName}
                handleRowClick={handleRowClick}
                funds={modelPortfolioFunds}
                modelPortfolios={nestedModelPortfolios}
                parentRowData={parentRowData}
                goBackToParentMp={goBackToParentMp}
              />
            </Grid>
          </div>
        </LocalizationProvider>
      )}
    </>
  );
};

export default compose(
  connect(
    (state: any) => ({
      investments:
        selectors.investmentAnalyserSelectors.researchInvestments(state),
      riskRatings: selectors.investmentAnalyserSelectors.riskRatings(state),
      riskRatingsCacheIsValid:
        selectors.investmentAnalyserSelectors.riskRatingsCacheIsValid(state),
      riskProfiler: selectors.firmSelectors.riskProfiler(state)
    }),
    {
      updateRiskRatings: actions.investmentAnalyserActions.updateRiskRatings
    }
  )
)(OverviewTabContent);
